import styled from '@emotion/styled';
import { css, keyframes } from '@emotion/react';
import { IconPostionType, ViewType, SizeType, ShapeType, Template } from './types';
import { helperAbsoluteFill, helperButtonReset, helperLinkReset } from '../../helpers';
import { fontSizeList, lineHeightList, paddingList, sizeList, templates } from './templates';
import { Colors } from '../Colors';
import { exceptStyledProps, getMediaProp, setDeclaration } from '../../utils';
import { Media } from '../../types';
import { Variables } from '../Variables';

export interface ContainerProps {
  view: ViewType | Template;
  size: SizeType | Media<SizeType>;
  shape?: ShapeType;
  isHover?: boolean;
  isLoading?: boolean;
  isSuccess?: boolean;
  isLong?: boolean;
  isPressed?: boolean;
  isDisabled?: boolean;
  isBlured?: boolean;
  isHeightAuto?: boolean;
}

export const buttonCover = css`
  ${helperButtonReset};
  ${helperLinkReset};
  vertical-align: middle;
`;

export const ButtonCover = styled('button')`
  ${buttonCover};
`;

export const Container = styled('button', exceptStyledProps('shape'))<ContainerProps>`
  ${helperButtonReset};
  ${helperLinkReset};
  cursor: ${props => getCursor(props)};
  overflow: visible;
  position: relative;
  display: inline-block;
  vertical-align: middle;
  width: ${props => props.isLong && '100%'};
  font-weight: 400;
  border-radius: ${props => getBorderRadius(props.shape)};
  backdrop-filter: ${props => props.isBlured && 'blur(20px)'};

  ${props => css`
    ${setDeclaration('height', props.isHeightAuto ? 'auto' : getSize(props.size))};
    ${setDeclaration('minHeight', props.isHeightAuto && getSize(props.size))};
    ${setDeclaration('padding', getPadding(props.size))};
    ${setDeclaration('fontSize', getFontSize(props.size))};
    ${setDeclaration('lineHeight', getLineHeight(props.size))};
  `};

  ${props => getShape(props)};
  ${props => getView(props.view).static};

  ${props =>
    props.isHover &&
    !props.isDisabled &&
    !props.isSuccess &&
    !props.isLoading &&
    css`
      &:hover,
      &:focus,
      ${ButtonCover}:hover &,
      ${ButtonCover}:focus & {
        ${getView(props.view).hover};
      }

      &:active {
        ${getView(props.view).active};
      }

      ${ButtonCover}:active & {
        ${getView(props.view).active};
      }

      &:focus-visible,
      ${ButtonCover}:focus-visible & {
        z-index: 2;
        background-clip: padding-box;
        border-color: transparent;
        box-shadow: none;

        &:before {
          box-shadow: 0 0 0 0.3rem
            ${getView(props.view).focus?.boxShadowColor ??
            getView(props.view).static.backgroundColor ??
            Colors.text.default};
        }
      }
    `};

  ${props =>
    (props.isPressed || props.isLoading) &&
    css`
      ${getView(props.view).active};
    `};

  /* :before принимает стиль фокуса */
  &:before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    border-radius: inherit;
    opacity: ${props => !getView(props.view).focus?.boxShadowColor && 0.4};
    transition: inherit;
  }
`;

interface InnerProps {
  isHidden?: boolean;
}

export const Inner = styled('div')<InnerProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  height: 100%;
  transition: opacity 0.3s, visibility 0.3s;

  ${props =>
    props.isHidden &&
    css`
      opacity: 0;
      visibility: hidden;
    `};
`;

interface IconProps {
  position?: IconPostionType;
}

export const Icon = styled('div')<IconProps>`
  line-height: 0;

  ${props =>
    props.position === 'left' &&
    css`
      margin-left: -0.4rem;
      margin-right: 0.6rem;
    `};

  ${props =>
    props.position === 'right' &&
    css`
      margin-left: 0.6rem;
      margin-right: -0.4rem;
    `};

  ${props =>
    props.position === 'left-auto' &&
    css`
      padding-right: 0.6rem;
      margin-right: auto;
      margin-left: -1rem;
    `};

  ${props =>
    props.position === 'right-auto' &&
    css`
      padding-left: 0.6rem;
      margin-left: auto;
      margin-right: -1rem;
    `};
`;

const showIconMiddle = keyframes`
  from {
    opacity: 0;
    transform: scale(2.5);
  }
`;

export const IconCenterWrapper = styled('div')`
  ${helperAbsoluteFill};
  width: auto;
  height: 74%;
  margin: auto;
  animation: ${showIconMiddle} 0.3s forwards;
`;

export const iconSvg = css`
  width: 100% !important;
  height: 100% !important;
`;

function getView(view: ContainerProps['view']): Template {
  if (typeof view === 'string') {
    return templates[view];
  }
  return view;
}

function getSize(size: ContainerProps['size']) {
  return getMediaProp(size, sizeList);
}

function getFontSize(size: ContainerProps['size']) {
  return getMediaProp(size, fontSizeList);
}

function getLineHeight(size: ContainerProps['size']) {
  return getMediaProp(size, lineHeightList);
}

function getPadding(size: ContainerProps['size']) {
  return getMediaProp(size, paddingList);
}

function getBorderRadius(shape?: ShapeType) {
  switch (shape) {
    case 'round':
      return '10rem';

    case 'circle':
      return '50%';

    default:
      return Variables.buttonRadius;
  }
}

function getShape(props: ContainerProps) {
  switch (props.shape) {
    case 'circle':
    case 'square':
      return css`
        ${setDeclaration('width', getSize(props.size))};
        padding: 0;
      `;

    default:
      return null;
  }
}

function getCursor(props: ContainerProps) {
  if (props.isDisabled) {
    return 'not-allowed';
  }

  if (props.isSuccess || !props.isHover) {
    return 'default';
  }

  if (props.isLoading) {
    return 'wait';
  }

  return null;
}
